import React, { useContext } from "react";
import LanguageContext from "../../utils/Contexts/LanguageContext";
import moment from "moment";
import Constant from "../Constants";
import ThemeContext from "../../utils/Contexts/ThemeContext";
import UserContext from "../../utils/Contexts/UserContext";
import axiosInstance from "../../utils/Requests/axios";
import toast from "react-hot-toast";
import { saveAs } from "file-saver";

const ExportTable = ({
  data,
  columnsVisibility,
  setColumnSorts,
  columnSorts,
}) => {
  const { t } = useContext(LanguageContext);
  const { userData } = useContext(UserContext);
  const { selectedTheme } = useContext(ThemeContext);

  const { recordStatusType, productType, importType, statusType } = Constant();
  const setColumnSort = (columnIndex) => {
    setColumnSorts((prevSorts) => ({
      ...prevSorts,
      [columnIndex]:
        prevSorts[columnIndex] === "asc"
          ? "desc"
          : prevSorts[columnIndex] === "desc"
          ? null
          : "asc",
    }));
  };

  const handleDownload = (data) => {
    const { product, uuid } = data;
    const url = `/exports/products/${product}/sellers/${userData?.uuid}/export/${uuid}/file`;
    axiosInstance
      .get(url)
      .then((response) => {
        saveAs(
          new Blob([response.data], { type: "application/octet-stream" }),
          "exports.csv",
          { autoBOM: false }
        );
      })
      .catch(() => {
        toast.error(t("There was an error fetching backlinks offers"));
      });
  };

  const columnsHeader = [
    { key: "date", label: t("Date"), isSortable: true, sortId: 0 },
    { key: "product", label: t("Product"), isSortable: true, sortId: 1 },
    { key: "type", label: t("Type"), isSortable: true, sortId: 2 },
    {
      key: "items_status",
      label: t("Items status"),
      isSortable: true,
      sortId: 3,
    },
    { key: "status", label: t("Status"), isSortable: true, sortId: 4 },
    { key: "items", label: t("Items"), isSortable: true, sortId: 5 },
    { key: "file_csv", label: t("File CSV"), isSortable: false },
  ];

  const formatDate = (date) => {
    if (!date) return "";
    const cleanDate = moment.utc(date);
    const now = moment.utc();
    const isRecent = now.diff(cleanDate, "minutes") <= 30;
    if (isRecent) {
      return cleanDate.fromNow();
    }
    return cleanDate.local().format("MMM DD, YYYY");
  };

  const columnHeaderValue = {
    date: {
      value: (item) => formatDate(item?.created),
      className: () => "text-dark",
      style: { fontSize: "16px", fontWeight: "400" },
    },
    product: {
      value: (item) => productType[item?.product],
      className: (item) =>
        `text-secondary bg-soft-secondary border-radius-5px d-flex justify-content-center align-items-center text-center`,
      style: { width: "180px", height: "29px" },
    },
    type: {
      value: (item) => importType[item?.type],
      className: (item) =>
        `text-secondary bg-soft-secondary border-radius-5px d-flex justify-content-center align-items-center text-center`,
      style: { width: "140px", height: "29px" },
    },
    items_status: {
      value: (item) => recordStatusType[item?.["items-status"] ?? 3]?.title,
      className: (item) =>
        `${
          recordStatusType[item?.["items-status"] ?? 3]?.class
        } border-radius-5px d-flex justify-content-center align-items-center  text-center`,
      style: { width: "140px", height: "29px" },
    },
    status: {
      value: (item) => statusType[item?.status]?.title,
      className: (item) =>
        `${
          statusType[item?.status]?.class
        } border-radius-5px d-flex justify-content-center align-items-center  text-center`,
      style: { width: "140px", height: "29px" },
    },
    items: {
      value: (item) => <span>{item?.items}</span>,
    },
    file_csv: {
      value: (item) => (
        // eslint-disable-next-line
        <a
          onClick={() => handleDownload(item)}
          className={`${
            item?.status !== "Completed"
              ? "border-primary text-primary"
              : "text-secondary"
          }`}
          style={{
            padding: "6px 10px",
            borderRadius: "5px",
            cursor: "pointer",
            border:
              item?.status !== "Completed"
                ? "1.5px solid"
                : "1.5px solid #1E2022",
          }}
        >
          {t("Download")}
        </a>
      ),
    },
  };

  return (
    <div className="table-responsive datatable-custom">
      <table
        id="datatable"
        className="table table-lg table-borderless table-thead-bordered table-nowrap table-align-middle card-table"
      >
        <thead className="thead-light">
          <tr>
            {columnsHeader.map(({ key, label, isSortable, sortId }) =>
              columnsVisibility[key] ? (
                <th
                  key={key}
                  className={`${isSortable ? "sorting" : ""}${
                    isSortable && !columnSorts[sortId]
                      ? ""
                      : columnSorts[sortId] === "asc"
                      ? "_asc"
                      : "_desc"
                  }`}
                  onClick={() => (isSortable ? setColumnSort(sortId) : {})}
                  style={{
                    color: selectedTheme === "dark" && "#FFFFFFCC",
                  }}
                >
                  {t(label)}
                </th>
              ) : null
            )}
            <th className="table-column-ps-0"></th>
          </tr>
        </thead>
        <tbody id="nav-one-eg1" aria-labelledby="nav-one-eg1-tab">
          {!!data?.length ? (
            data.map((item, index) => (
              <tr
                key={index}
                className={index === data.length - 1 ? "" : "border-bottom"}
              >
                {Object.entries(columnsVisibility)?.map(
                  ([key, isVisible]) =>
                    isVisible && (
                      <td key={key}>
                        <span
                          style={{
                            ...columnHeaderValue[key]?.style,
                            display: "inline-block",
                          }}
                          className={`${columnHeaderValue[key]?.className?.(
                            item
                          )}`}
                        >
                          {columnHeaderValue[key]?.value(item)}
                        </span>
                      </td>
                    )
                )}
                <td className="p-1">
                  <div className="dropdowm">
                    <button
                      type="button"
                      className="btn btn-ghost-secondary btn-icon btn-sm rounded-circle"
                      id="teamsDropdown2"
                      data-bs-toggle="dropdown"
                      aria-expanded="false"
                    >
                      <i className="bi-three-dots-vertical"></i>
                    </button>

                    <div
                      className="dropdown-menu dropdown-menu-sm dropdown-menu-end"
                      aria-labelledby="teamsDropdown2"
                    >
                      <button
                        className="dropdown-item"
                        // data-bs-toggle="modal"
                        // data-bs-target="#modal-sm-add-remove-to-lists"
                        onClick={() => handleDownload(item)}
                      >
                        {t("Download")}
                      </button>
                    </div>
                  </div>
                </td>
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan="100%">
                <div className="text-center p-4 no-data-wrapper">
                  <img
                    className="mb-3"
                    src="https://static.articlesfactory.ai/app/svg/illustrations-light/oc-error.svg"
                    alt=""
                    style={{ width: "10rem" }}
                    data-hs-theme-appearance="dark"
                  />
                  <img
                    className="mb-3"
                    src="https://static.articlesfactory.ai/app/svg/illustrations/oc-error.svg"
                    alt=""
                    style={{ width: "10rem" }}
                    data-hs-theme-appearance="default"
                  />
                  <p className="mb-0">
                    {!data ? t("Loading data") : t("No data to show")}
                  </p>
                </div>
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
};

export default ExportTable;
